import React, { useState } from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import type { GetServerSideProps } from 'next';

import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import Loader from 'src/components/ui/Loader';
import { Tabs, Tab } from 'src/components/ui/Tabs';
import SeeAllHeaderProfile from 'src/components/profile/SeeAllHeaderProfile';
import PropertyCard from 'src/components/ad/PropertyCard';
import FullWidthLayout from 'src/components/other/FullWidthLayout';
import type { UserPageProps } from 'src/pages/user/[userId]/index';

import useAppRouter from 'src/hooks/useAppRouter';
import useInfiniteScrollQuery from 'src/hooks/useInfiniteScrollQuery';

import { useUserProfileQuery, useUserAdsQuery } from 'src/hooks/__generated__/queries';
import type { Ad, UserProfileQuery } from 'src/types/__generated__/graphql';
import { AdStatuses, UserProfile } from 'src/types/__generated__/graphql';

import { getAuthCookie } from 'src/utils/authCookie';
import getApolloClient from 'src/utils/getApolloClient';
import { useAdScrollRestoration } from 'src/hooks/useAdScrollRestoration';
import { defaultAdHref } from 'src/components/ad/PropertyCardLink';
import useBreakpoints from 'src/hooks/useBreakpoints';

type TabTypes = AdStatuses.Available | AdStatuses.Ended;

const defaultLimit = 12;

const UserAdsPage: PageComponent = () => {
    const { t } = useTranslation(['common', 'profile']);

    const router = useAppRouter();

    const { isMobile } = useBreakpoints();

    const userId = router.query.userId as string;
    const tab = router.query.tab as TabTypes | undefined;

    const scrollRestorationQueryKey = 'restoreUserShowcaseScrollTo';
    const { shouldRestoreScroll, scrollRestorationRef } = useAdScrollRestoration<HTMLDivElement>({
        scrollRestorationQueryKey,
    });

    const [selectedTab, setSelectedTab] = useState<TabTypes>(tab ?? AdStatuses.Available);

    const { previousData, data: profile = previousData } = useUserProfileQuery({
        variables: {
            id: userId,
        },
        skip: !userId,
        fetchPolicy: 'cache-and-network',
    });

    const totalAds =
        (selectedTab === AdStatuses.Available
            ? profile?.userProfile?.numberOfAvailableAds
            : profile?.userProfile?.numberOfEndedAds) ?? 0;
    const fetchPolicy = shouldRestoreScroll ? 'cache-first' : 'cache-and-network';

    const { data, loading, scrollBottomRef } = useInfiniteScrollQuery({
        query: useUserAdsQuery({
            variables: {
                userId,
                offset: 0,
                limit: defaultLimit,
                statuses: [selectedTab],
            },
            fetchPolicy,
            skip: !userId,
            notifyOnNetworkStatusChange: true,
        }),
        hasMoreData: ({ userAds }) => (userAds?.length ?? -1) < totalAds,
        fetchMoreVariables: ({ userAds }) => ({ offset: userAds?.length ?? 0 }),
    });

    const handleTabChange = (_ev: React.SyntheticEvent, newTab: TabTypes) => {
        setSelectedTab(newTab);

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace({ pathname: router.pathname, query: { ...router.query, tab } });
    };

    return (
        <Container>
            <SeeAllHeaderProfile titleTranslationKey="profile:showcase" />
            {profile?.userProfile ? (
                <Box mt={3} display="inline-block">
                    <Tabs value={selectedTab} onChange={handleTabChange}>
                        <Tab
                            label={
                                isMobile
                                    ? t('profile:availableAds')
                                    : t('profile:availableAdsCount', {
                                          count: profile.userProfile.numberOfAvailableAds,
                                      })
                            }
                            value={AdStatuses.Available}
                        />
                        <Tab
                            label={
                                isMobile
                                    ? t('profile:endedAds')
                                    : t('profile:endedAdsCount', {
                                          count: profile.userProfile.numberOfEndedAds,
                                      })
                            }
                            value={AdStatuses.Ended}
                        />
                    </Tabs>
                </Box>
            ) : (
                <Loader />
            )}
            <Box mb={4} mt={2}>
                {data?.userAds ? (
                    <Grid spacing={3} container>
                        {data.userAds.map((ad) => (
                            <Grid key={ad.id} xs={12} sm={6} md={4} lg={4} xl={3} item>
                                <PropertyCard
                                    ref={scrollRestorationRef(ad)}
                                    ad={ad as Ad}
                                    buildAdHref={(a) => `${defaultAdHref(a)}?${scrollRestorationQueryKey}=true`}
                                    showTags
                                    isDarkCard
                                />
                            </Grid>
                        ))}
                    </Grid>
                ) : null}
                <Box ref={scrollBottomRef} />
                {loading ? (
                    <Box height="3rem" width="100%" mt="1rem">
                        <Loader />
                    </Box>
                ) : null}
            </Box>
        </Container>
    );
};

UserAdsPage.Layout = FullWidthLayout;

export const getServerSideProps: GetServerSideProps<UserPageProps> = async (context) => {
    const auth = getAuthCookie(context);
    const apolloClient = getApolloClient(null, auth);
    const userId = context.params?.userId as string | undefined;
    let data;
    if (userId) {
        const result = await apolloClient.query<UserProfileQuery>({
            query: UserProfile,
            variables: {
                id: userId,
            },
        });
        data = result.data;
    }

    return {
        props: {
            user: data?.userProfile,
            ...(context.locale
                ? await serverSideTranslations(context.locale, ['common', 'profile', 'ad', 'favorites'])
                : {}),
            SEOProps: {
                pageTitleTranslation: {
                    key: 'common:seo.user.showcase',
                    params: {
                        name: String(data?.userProfile?.fullName),
                    },
                },
            },
        },
    };
};

export default UserAdsPage;
